import React, { useState } from 'react';
import { Button, Col, Row, Tabs, Tab, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { formatPrice } from '../../../../shared/utils/formatPrice';
import { calculateNightsText, calculateNights } from '../../../../shared/utils/dateUtils';
import { BookingPayment } from '../../../../shared/types/booking/BookingPaymentType';
import { IconBed, IconCalendarEvent, IconMoon, IconUser, IconMessageCircle, IconEdit, IconCheck, IconCoin, IconX, IconPlus, IconArrowsExchange, IconArrowsSplit, IconArrowMerge, IconCopy, IconAlertTriangleFilled } from '@tabler/icons-react';
import { upperCase } from 'lodash';
import { toast } from 'sonner';
import clsx from 'clsx';

import OtaIcon from './OtaIcon';
import './TooltipContent.css';
import { NAVIGATION_URLS } from '../../../../shared/utils/urls';
import { useAuth } from '../../../../auth/context/AuthProvider';
import { useViewport } from '../../../../shared/hooks/useViewport';
import { usePropertyReadOnly } from '../../../../shared/hooks/usePropertyReadOnly';
import ReadOnlyGuard from '../../../../shared/components/ReadOnlyGuard';

// Tipe data (tidak ada perubahan)
interface BookingData {
    id: string;
    invoice_number: string;
    voucher_number?: string | null;
    payment_type?: string | null;
    customer_name: string;
    customer_surname?: string | null;
    customer_email?: string | null;
    customer_phone?: string | null;
    room_type?: { name: string } | null;
    room?: { room_number: string } | null;
    night?: number | null;
    rate_plan?: { name: string } | null;
    // Dipakai HANYA untuk menentukan tampil-tidaknya tombol "Fix it" (booking
    // belum lengkap mapping-nya) -- lihat ShowBookingResponse.ts DataSchema,
    // field-field ini scalar nullable di root object (bukan di dalam
    // room_type/room/rate_plan).
    room_type_id?: string | null;
    rate_plan_id?: string | null;
    room_id?: string | null;
    checkin_date: string;
    checkout_date: string;
    total_rate: string | number;
    down_payment?: string | number | null;
    due_payment?: string | number | null;
    total_extra?: string | number | null;
    total_with_extras?: string | number | null;
    /** Ada minimal 1 extra AKTIF (bukan yang sudah dibatalkan) -- untuk badge tombol "+ Extra". */
    has_extra?: boolean | null;
    public_note?: string | null;
    private_note?: string | null;
    /** Booking hasil bulk-import di channel manager, bukan reservasi normal. */
    is_imported?: boolean | null;
    split_date?: string | null;
    /** Terisi kalau baris ini segmen split; menentukan apakah tombol Merge muncul. */
    split_role?: 'segment_1' | 'segment_2' | null;
    status?: string | null;
    ota_name?: string | null;
    adults: number;
    children: number;
}

interface TooltipContentNew1Props {
    booking: { data: BookingData };
    onUpdate: (id: string) => void;
    onFixIt?: (id: string) => void;
    onComment: (id: string) => void;
    onUpdateStatus: (id: string) => void;
    onAddPayment: (id: string) => void;
    onAddExtra: (id: string) => void;
    onSwitch?: (id: string) => void;
    onSplit?: (id: string) => void;
    onMerge?: (id: string) => void;
    onClose: () => void;
}

// Komponen pembantu (tidak ada perubahan)
const InfoItem: React.FC<{ label: string; value: React.ReactNode; className?: string }> = ({ label, value }) => (
    <div className="d-flex justify-content-between align-items-start gap-3 mb-2">
        {/* BS5: Menggantikan .label */}
        <div className="text-muted small flex-shrink-0">{label}</div>
        {/* BS5: Menggantikan .value */}
        <div className="fw-semibold text-end" style={{ wordBreak: 'break-all' }}>{value}</div>
    </div>
);

/** Anggap catatan "ada" hanya kalau bukan string kosong/spasi — konsisten dengan indikator di hover tooltip (BookingHoverTooltip.tsx). */
const hasText = (value?: string | null): boolean => typeof value === 'string' && value.trim() !== '';

/**
 * Judul tab. Di mobile (<768px, sama dengan breakpoint yang dipakai
 * EventContent.tsx untuk switch Popover→Offcanvas) label teks disembunyikan,
 * hanya ikon yang tampil — 3 tab dengan teks penuh ("Guest Info", "Room &
 * Payment", "Comments") jadi sempit di layar HP. Label tetap ada lewat
 * atribut `title` (native tooltip on long-press/hover) untuk aksesibilitas.
 */
const TabTitle: React.FC<{ icon: React.ReactNode; label: string; isMobile: boolean; children?: React.ReactNode }> = ({ icon, label, isMobile, children }) => (
    <span className="d-flex align-items-center" title={label}>
        {icon}
        {!isMobile && <span className="ms-1">{label}</span>}
        {children}
    </span>
);

const TooltipContentNew1: React.FC<TooltipContentNew1Props> = ({ booking, onUpdate, onFixIt, onComment, onUpdateStatus, onAddPayment, onAddExtra, onSwitch, onSplit, onMerge, onClose }) => {
    const data = booking.data;
    const { hasPermission } = useAuth();
    const { isMobile } = useViewport();
    // Komponen ini dipakai bersama oleh popover kalender DAN BookingDetailModalV2
    // (global, lewat Sidebar). Hook-nya membaca auth state live, jadi aman
    // dipanggil di sini — gating ikut aktif di semua entry point, dan tidak
    // berpengaruh sama sekali selama property aktif masih aktif.
    const { isReadOnly } = usePropertyReadOnly();
    const paymentLabel = BookingPayment.find(p => p.value === data.payment_type)?.label ?? 'N/A';
    const hasPublicNote = hasText(data.public_note);
    const hasPrivateNote = hasText(data.private_note);
    // Booking belum lengkap (mis. hasil sync Channex yang belum di-mapping) --
    // tombol "Fix it" hanya muncul untuk kasus ini.
    const isIncompleteBooking = !data.room_type_id || !data.rate_plan_id || !data.room_id;

    // Dibuat sebagai array supaya lebar tiap badge bisa dibagi rata otomatis
    // sesuai JUMLAH badge yang tampil (bisa 2 atau 3 — IMPORTED hanya muncul
    // untuk booking hasil import). Lihat `flex: 1 1 0` di tempat render-nya.
    const statusBadges = [
        { key: 'payment', label: paymentLabel, variant: 'primary' },
        { key: 'status', label: upperCase(data.status || 'N/A'), variant: 'success' },
        ...(data.is_imported ? [{ key: 'imported', label: 'IMPORTED', variant: 'warning' }] : []),
    ];

    // BARU: State untuk mengontrol tab yang aktif
    const [activeTab, setActiveTab] = useState('guest');
    const [copiedType, setCopiedType] = useState<string | null>(null);

    const handleCopy = (text: string, type: string, label: string) => {
        const cleanText = text.replace(/^#/, '');
        navigator.clipboard.writeText(cleanText);
        toast.success(`${label} copied to clipboard`);
        setCopiedType(type);
        setTimeout(() => setCopiedType(null), 2000);
    };

    return (
        <div className="tooltip-final-design">
            {/* Header Biru */}
            <div className="tooltip-header bg-primary bg-gradient text-white p-3">
                {/* KOMENTAR: Tetap menggunakan d-flex agar bisa ditargetkan oleh CSS responsif */}
                <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center">
                        <OtaIcon otaName={data.ota_name ?? undefined} size={24} className="ota-logo me-3" />
                        <div>
                            <h5 className="mb-0">Reservation Information</h5>
                            {/* <span className="invoice-number">#{data.voucher_number || data.invoice_number}</span> */}
                            <div className="d-flex align-items-center gap-1 copy-container" onClick={() => handleCopy(booking.data.invoice_number, 'invoice', 'Invoice')}>
                                <a href={`${NAVIGATION_URLS.DASHBOARD_ADMIN_RESERVATION_INVOICE_VIEW}/${booking.data.invoice_number}`} target='_blank' className="invoice-number text-white text-decoration-none">
                                    #{booking.data.invoice_number}
                                </a>
                                <button className={`btn btn-link p-0 btn-copy-mini ${copiedType === 'invoice' ? 'text-white' : 'text-white-50'}`}>
                                    {copiedType === 'invoice' ? <IconCheck size={14} /> : <IconCopy size={14} />}
                                </button>
                            </div>
                            {booking.data.voucher_number && (
                                <div className="d-flex align-items-center gap-1 copy-container" onClick={() => handleCopy(booking.data.voucher_number || '', 'voucher', 'Voucher')}>
                                    <a href={`${NAVIGATION_URLS.DASHBOARD_ADMIN_RESERVATION_INVOICE_VOUCHER}/${booking.data.voucher_number}`} target='_blank' className='invoice-number text-white text-decoration-none'>
                                        #{booking.data.voucher_number}
                                    </a>
                                    <button className={`btn btn-link p-0 btn-copy-mini ${copiedType === 'voucher' ? 'text-white' : 'text-white-50'}`}>
                                        {copiedType === 'voucher' ? <IconCheck size={14} /> : <IconCopy size={14} />}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                    {/* KOMENTAR: Tambahkan kelas khusus mobile untuk penargetan yang lebih mudah */}
                    <button
                        className="btn btn-icon btn-sm text-white btn-close-mobile"
                        onClick={onClose}
                        aria-label="Close"
                        style={{ background: 'transparent', border: 'none' }}
                    >
                        <IconX size={22} />
                    </button>
                </div>
            </div>

            {/* ==================================================================== */}
            {/* Ringkasan: 3 kartu info sejajar + baris badge status di bawahnya      */}
            {/* ==================================================================== */}
            <div className="p-3 border-bottom">
                {/* Tiga kartu selalu sama lebar (sm=4 masing-masing). Di layar kecil
                    otomatis menumpuk penuh supaya isinya tidak terhimpit. */}
                <Row className="g-2">
                    <Col xs={12} sm={4}>
                        <div className="d-flex align-items-center gap-2 bg-light rounded-3 p-2 h-100">
                            <div className="bg-success-subtle rounded-3 d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: '38px', height: '38px' }}>
                                <IconCalendarEvent size={20} className="text-success" />
                            </div>
                            <div style={{ minWidth: 0 }}>
                                <small className="text-muted d-block lh-1">Check-in</small>
                                <div className="fw-bold text-truncate">{data.checkin_date}</div>
                            </div>
                        </div>
                    </Col>
                    <Col xs={12} sm={4}>
                        <div className="d-flex align-items-center gap-2 bg-light rounded-3 p-2 h-100">
                            <div className="bg-danger-subtle rounded-3 d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: '38px', height: '38px' }}>
                                <IconCalendarEvent size={20} className="text-danger" />
                            </div>
                            <div style={{ minWidth: 0 }}>
                                <small className="text-muted d-block lh-1">Check-out</small>
                                <div className="fw-bold text-truncate">{data.checkout_date}</div>
                            </div>
                        </div>
                    </Col>
                    <Col xs={12} sm={4}>
                        <div className="d-flex align-items-center gap-2 bg-light rounded-3 p-2 h-100">
                            <div className="bg-info-subtle rounded-3 d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: '38px', height: '38px' }}>
                                <IconMoon size={20} className="text-info" />
                            </div>
                            <div style={{ minWidth: 0 }}>
                                <small className="text-muted d-block lh-1">Duration</small>
                                <div className="fw-bold text-truncate">{calculateNightsText(data.night ?? 0)}</div>
                            </div>
                        </div>
                    </Col>
                </Row>

                {/* Badge status. `flex: 1 1 0` (bukan flex-fill yang basis-nya auto)
                    membuat lebarnya dibagi RATA sesuai jumlah badge — 2 badge jadi
                    50% masing-masing, 3 badge jadi ~33%. `minWidth: 0` + text-truncate
                    supaya label panjang terpotong rapi di dalam kotaknya sendiri,
                    bukan meluber keluar. */}
                <div className="d-flex gap-2 mt-2">
                    {statusBadges.map((badge) => (
                        <div
                            key={badge.key}
                            title={badge.label}
                            className={`d-flex align-items-center justify-content-center gap-2 rounded-3 py-2 px-3 fw-semibold bg-${badge.variant}-subtle text-${badge.variant}`}
                            style={{ flex: '1 1 0', minWidth: 0 }}
                        >
                            <span className="rounded-circle flex-shrink-0" style={{ width: 8, height: 8, backgroundColor: 'currentColor' }} />
                            <span className="text-truncate">{badge.label}</span>
                        </div>
                    ))}
                </div>
            </div>
            {/* ==================================================================== */}

            {/* Konten Utama 3 Kolom */}
            {/* ==================================================================== */}
            {/* PEROMBAKAN UTAMA: Mengganti 3 Kolom dengan Navigasi Tab */}
            {/* ==================================================================== */}
            <div className="tooltip-scrollable-content">
                <div className="px-3 pb-3 pt-2">
                    <Tabs
                        id="reservation-tooltip-tabs"
                        activeKey={activeTab}
                        onSelect={(k) => setActiveTab(k as string)}
                        className="custom-tooltip-tabs mb-3" // Class baru untuk styling
                        variant="underline" // Tampilan tab yang lebih modern
                    >
                        <Tab
                            eventKey="guest"
                            title={<TabTitle icon={<IconUser size={16} className={isMobile ? '' : 'me-1'} />} label="Guest Info" isMobile={isMobile} />}
                        >
                            <div className="tab-pane-content">
                                <InfoItem label="Guest Name" value={`${data.customer_name} ${data.customer_surname || ''}`} />
                                <InfoItem label="Voucher" value={data.voucher_number ?? 'N/A'} />
                                <InfoItem label="Payment" value={paymentLabel} />
                                <hr />
                                <InfoItem label="Name" value={data.customer_name} />
                                <InfoItem label="Email" value={<a href={`mailto:${data.customer_email}`}>{data.customer_email}</a>} />
                                <InfoItem label="Phone" value={<a href={`tel:${data.customer_phone}`}>{data.customer_phone}</a>} />
                                <InfoItem label="Adults" value={data.adults} />
                                <InfoItem label="Children" value={data.children} />
                            </div>
                        </Tab>
                        <Tab
                            eventKey="room"
                            title={<TabTitle icon={<IconBed size={16} className={isMobile ? '' : 'me-1'} />} label="Room & Payment" isMobile={isMobile} />}
                        >
                            <div className="tab-pane-content">
                                <InfoItem label="Room Type" value={data.room_type?.name ?? 'N/A'} />
                                <InfoItem label="Room Number" value={data.room?.room_number ?? 'N/A'} />
                                <InfoItem label="Rate Plan" value={data.rate_plan?.name ?? 'N/A'} />
                                <hr />
                                <InfoItem label="Room" value={formatPrice(Number(data.total_rate), 'IDR')} />
                                <InfoItem label="Extra" value={formatPrice(Number(data.total_extra ?? 0), 'IDR')} />
                                <InfoItem
                                    label="Total Bill"
                                    value={<strong>{formatPrice(Number(data.total_with_extras ?? data.total_rate), 'IDR')}</strong>}
                                />
                                <InfoItem
                                    label="Paid"
                                    value={<span className="text-success fw-bold">{formatPrice(Number(data.down_payment ?? 0), 'IDR')}</span>}
                                />
                                <InfoItem
                                    label="Due Amount"
                                    value={<strong className="text-danger">{formatPrice(Number(data.due_payment ?? 0), 'IDR')}</strong>}
                                />
                            </div>
                        </Tab>
                        <Tab
                            eventKey="comments"
                            title={
                                <TabTitle icon={<IconMessageCircle size={16} className={isMobile ? '' : 'me-1'} />} label="Comments" isMobile={isMobile}>
                                    {/* Flag ringkas supaya user notis ada catatan tanpa harus buka tab ini
                                        dulu — hijau utk public note, merah utk private note, sejajar dengan
                                        warna indikator yang sama di hover tooltip (BookingHoverTooltip.tsx).
                                        Tetap tampil di mode icon-only (mobile), menempel di sisi ikon. */}
                                    {hasPublicNote && (
                                        <span
                                            className="rounded-circle bg-success ms-1"
                                            style={{ width: 6, height: 6, flexShrink: 0 }}
                                            title="Has public note"
                                        />
                                    )}
                                    {hasPrivateNote && (
                                        <span
                                            className="rounded-circle bg-danger ms-1"
                                            style={{ width: 6, height: 6, flexShrink: 0 }}
                                            title="Has private note"
                                        />
                                    )}
                                </TabTitle>
                            }
                        >
                            <div className="tab-pane-content">
                                <h6>Public Comment</h6>
                                <div className="bg-light border-start border-3 border-secondary-subtle p-2 rounded mb-3">
                                    <p className="m-0 fst-italic text-muted small">{hasPublicNote ? data.public_note : 'No public comment.'}</p>
                                </div>
                                <h6>Private Comment</h6>
                                <div className="bg-light border-start border-3 border-danger p-2 rounded">
                                    <p className="m-0 fst-italic text-danger small">{hasPrivateNote ? data.private_note : 'No private comment.'}</p>
                                </div>
                            </div>
                        </Tab>
                    </Tabs>
                </div>
            </div>
            {/* ==================================================================== */}

            {/* ==================================================================== */}
            {/* PERBAIKAN: Layout Footer dengan Hirarki Visual yang Lebih Jelas */}
            {/* ==================================================================== */}
            <div className="p-3 bg-light border-top">
                <div className="d-flex flex-column gap-3">
                    {/* Baris Pertama: Operasional Kamar & Finansial */}
                    <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                        {/* Grup Operasional */}
                        <div className="d-flex flex-wrap gap-2">
                            {onSwitch && (
                                <ReadOnlyGuard>
                                    <Button variant='outline-primary' size="sm" onClick={() => onSwitch(data.id)}>
                                        <IconArrowsExchange size={14} className="me-1" /> Switch
                                    </Button>
                                </ReadOnlyGuard>
                            )}
                            {onSplit && (() => {
                                const isSplitableDuration = calculateNights(data.checkin_date, data.checkout_date) > 1;

                                const SplitButton = (
                                    <Button
                                        variant='outline-primary'
                                        size="sm"
                                        onClick={() => onSplit(data.id)}
                                        disabled={!isSplitableDuration}
                                        style={!isSplitableDuration ? { pointerEvents: 'none' } : {}}
                                    >
                                        <IconArrowsSplit size={14} className="me-1" /> Split
                                    </Button>
                                );

                                if (!isSplitableDuration) {
                                    return (
                                        <ReadOnlyGuard>
                                            <OverlayTrigger
                                                placement="top"
                                                overlay={<Tooltip id="tooltip-split-duration">Split booking requires a minimum duration of 2 nights.</Tooltip>}
                                            >
                                                <span className="d-inline-block">
                                                    {SplitButton}
                                                </span>
                                            </OverlayTrigger>
                                        </ReadOnlyGuard>
                                    );
                                }

                                return <ReadOnlyGuard>{SplitButton}</ReadOnlyGuard>;
                            })()}
                            {onMerge && data.split_role && (
                                <ReadOnlyGuard>
                                    <Button variant='outline-primary' size="sm" onClick={() => onMerge(data.id)}>
                                        <IconArrowMerge size={14} className="me-1" /> Merge
                                    </Button>
                                </ReadOnlyGuard>
                            )}
                        </div>

                        {/* Grup Finansial & Status */}
                        <div className="d-flex flex-wrap gap-2">
                            <ReadOnlyGuard>
                                <Button variant='info' size="sm" className='text-white' onClick={() => onAddPayment(data.id)}>
                                    <IconCoin size={14} className="me-1" /> Payment
                                </Button>
                            </ReadOnlyGuard>
                            <ReadOnlyGuard>
                                <Button variant='warning' size="sm" className='text-white position-relative' onClick={() => onAddExtra(data.id)}>
                                    <IconPlus size={14} className="me-1" /> Extra
                                    {/* Titik indikator: reservasi ini sudah punya minimal 1 extra
                                        aktif -- supaya staf tahu tanpa perlu buka dialog Extra dulu. */}
                                    {data.has_extra && (
                                        <span
                                            className="position-absolute top-0 start-100 translate-middle p-1 bg-primary border border-light rounded-circle"
                                            title="This reservation already has extra/service"
                                        >
                                            <span className="visually-hidden">Has extra</span>
                                        </span>
                                    )}
                                </Button>
                            </ReadOnlyGuard>
                            <ReadOnlyGuard>
                                <Button variant="success" size="sm" onClick={() => onUpdateStatus(data.id)}>
                                    <IconCheck size={14} className="me-1" /> Status
                                </Button>
                            </ReadOnlyGuard>
                        </div>
                    </div>

                    {/* Baris Kedua: Komentar & Aksi Utama. Di mobile ditumpuk penuh-lebar
                        (bukan berdampingan) — dua tombol berdampingan ("Comment" +
                        "Edit Reservation") gampang kepepet/overflow di layar sempit, dan
                        target-tap penuh-lebar lebih nyaman disentuh jari. */}
                    <div className={clsx('d-flex align-items-center gap-2 pt-2 border-top', isMobile ? 'flex-column' : 'justify-content-between')}>
                        <ReadOnlyGuard>
                            <Button variant="outline-secondary" size="sm" className={clsx(isMobile && 'w-100')} onClick={() => onComment(data.id)}>
                                <IconMessageCircle size={16} className='me-1' /> Comment
                            </Button>
                        </ReadOnlyGuard>

                        {isIncompleteBooking && (
                            <ReadOnlyGuard>
                                <Button variant="warning" size="sm" className={clsx('text-white', isMobile && 'w-100')} onClick={() => onFixIt?.(data.id)}>
                                    <IconAlertTriangleFilled size={16} className="me-1" /> Fix it
                                </Button>
                            </ReadOnlyGuard>
                        )}

                        {/* Tombol ini TIDAK digating: membuka reservasi untuk DILIHAT
                            tetap boleh saat property nonaktif — labelnya saja yang
                            turun jadi "View Reservation" supaya tidak menjanjikan edit. */}
                        <Button variant="primary" size="sm" className={clsx('px-4', isMobile && 'w-100')} onClick={() => onUpdate(data.id)}>
                            <IconEdit size={16} className="me-1" /> {hasPermission('booking_update') && !isReadOnly ? 'Edit Reservation' : 'View Reservation'}
                        </Button>
                    </div>
                </div>
            </div>
            {/* ==================================================================== */}
        </div>
    );
};

export default TooltipContentNew1;